import { useState } from 'react';
import { Modal, Input, Button } from '@react-apps/design-system';
import { PROVIDER_NAME, useLoginMethods } from '../hooks/use-login-methods';
import type {
  AuthMethod,
  FederatedMethod,
  LinkedAuthMethod,
  MeResponse,
} from '../../domain/entities/auth.entity';

const METHOD_LABEL: Record<AuthMethod, string> = {
  password: 'E-mail e senha',
  ...PROVIDER_NAME,
};

// Microsoft fora até a configuração do provedor estar pronta
// (login-com-microsoft.md § Validação): sem ele habilitado, o popup só erraria.
// Voltar é repor `'microsoft.com'` aqui.
const FEDERATED_METHODS: FederatedMethod[] = ['google.com'];

interface LoginMethodsCardProps {
  /** Formas de login vinculadas, como vieram do último `GET /auth/me`. */
  methods: LinkedAuthMethod[];
  /** Recebe a conta já atualizada após vincular/desvincular. */
  onChanged: (me: MeResponse) => void;
}

/**
 * Card "Formas de login" da página de Segurança (google-sign-in §3.6): lista os
 * métodos vinculados, vincula Google ou Microsoft e remove um método. A conta nunca fica
 * sem nenhum — o botão da última forma vem desabilitado, e o backend recusa de
 * qualquer jeito (CANNOT_UNLINK_LAST_IDENTITY), caso o client deixe passar.
 */
export function LoginMethodsCard({
  methods,
  onChanged,
}: LoginMethodsCardProps) {
  const { link, unlink, isBusy, linking, error, clearError } =
    useLoginMethods(onChanged);
  const [pendingUnlink, setPendingUnlink] = useState<AuthMethod | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [linkedNotice, setLinkedNotice] = useState<string | null>(null);

  const has = (method: AuthMethod) => methods.some((m) => m.method === method);
  const hasPassword = has('password');
  const isLast = methods.length <= 1;
  // Sem senha, a reautenticação é o popup do federado — o mesmo que o
  // repositório escolhe (Google antes de Microsoft).
  const reauthProvider =
    PROVIDER_NAME[has('google.com') ? 'google.com' : 'microsoft.com'];

  const startLink = async (method: FederatedMethod) => {
    setLinkedNotice(null);
    if (await link(method)) {
      setLinkedNotice(
        `Login com ${PROVIDER_NAME[method]} vinculado à sua conta.`,
      );
    }
  };

  const closeModal = () => {
    setPendingUnlink(null);
    setCurrentPassword('');
    clearError();
  };

  const startUnlink = (method: AuthMethod) => {
    clearError();
    setLinkedNotice(null);
    setCurrentPassword('');
    setPendingUnlink(method);
  };

  const confirmUnlink = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!pendingUnlink) return;
    // Sem senha na conta, a prova de posse é o popup do federado.
    const ok = await unlink(
      pendingUnlink,
      hasPassword ? currentPassword : undefined,
    );
    if (ok) closeModal();
  };

  return (
    <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-6">
      <div className="flex items-start gap-4">
        <div className="bg-(--color-accent)/10 text-(--color-accent) flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
          <LinkIcon />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-(--color-text-primary) text-base font-semibold">
            Formas de login
          </h2>
          <p className="text-(--color-text-secondary) mt-0.5 text-sm">
            Como você entra na sua conta. Mantenha ao menos uma forma ativa.
          </p>

          <ul className="mt-4 space-y-2">
            {methods.map((m) => (
              <li
                key={m.method}
                className="border-(--color-border) flex items-center justify-between gap-4 rounded-lg border px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-(--color-text-primary) text-sm font-medium">
                    {METHOD_LABEL[m.method] ?? m.method}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => startUnlink(m.method)}
                  disabled={isLast || isBusy}
                  title={
                    isLast
                      ? 'Vincule outra forma de login antes de remover esta.'
                      : undefined
                  }
                  className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Desvincular
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex flex-wrap gap-2">
            {FEDERATED_METHODS.filter((m) => !has(m)).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => void startLink(m)}
                disabled={isBusy}
                aria-busy={linking === m}
                className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {linking === m ? 'Vinculando…' : `Vincular ${PROVIDER_NAME[m]}`}
              </button>
            ))}
          </div>

          {linkedNotice && !error && (
            <p
              role="status"
              className="text-(--color-text-secondary) mt-3 text-sm"
            >
              {linkedNotice}
            </p>
          )}

          {error && pendingUnlink === null && (
            <div
              role="alert"
              className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
            >
              {error}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={pendingUnlink !== null}
        onClose={closeModal}
        title="Desvincular forma de login"
        size="sm"
      >
        <form onSubmit={(e) => void confirmUnlink(e)} className="space-y-4">
          <p className="text-(--color-text-secondary) text-sm">
            {pendingUnlink && (
              <>
                Você não poderá mais entrar por{' '}
                <strong>{METHOD_LABEL[pendingUnlink] ?? pendingUnlink}</strong>.
                As outras formas continuam funcionando.
              </>
            )}
          </p>

          {hasPassword ? (
            <>
              <p className="text-(--color-text-secondary) text-sm">
                Para sua segurança, confirme sua senha atual.
              </p>
              <Input
                id="unlinkCurrentPassword"
                label="Senha atual"
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
              />
            </>
          ) : (
            <p className="text-(--color-text-secondary) text-sm">
              Para sua segurança, confirme sua identidade na janela do{' '}
              {reauthProvider} que vamos abrir.
            </p>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={closeModal}
              disabled={isBusy}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isBusy || (hasPassword && !currentPassword)}
            >
              {isBusy ? 'Removendo…' : 'Desvincular'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function LinkIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
      />
    </svg>
  );
}
